import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { serviceConfigApi } from '../api/serviceConfigApi'
import type {
  FetchByReferenceResponse,
  FetchByReferenceResult,
  FetchFromDateResponse,
  ManualFetchResponse,
  RecoveryResponse,
} from '../api/serviceConfigApi'
import { DONATIONS } from '@/features/donations/constants/endpoints'
import { useAuthStore } from '@/features/auth/store/authStore'

// Query Keys
export const serviceConfigKeys = {
  all: ['serviceConfig'] as const,
  config: () => [...serviceConfigKeys.all, 'config'] as const,
  status: () => [...serviceConfigKeys.all, 'status'] as const,
}

// Queries
export const useServiceConfig = () => {
  return useQuery({
    queryKey: serviceConfigKeys.config(),
    queryFn: serviceConfigApi.getConfig,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  })
}

export const useServiceStatus = () => {
  return useQuery({
    queryKey: serviceConfigKeys.status(),
    queryFn: serviceConfigApi.getServiceStatus,
    staleTime: 1000 * 60 * 2, // 2 minutes
    gcTime: 1000 * 60 * 5, // 5 minutes
  })
}

// Mutations
export const useUpdateConfig = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: serviceConfigApi.updateConfig,
    onSuccess: (_, variables) => {
      toast.success(
        `Configuration Updated - Email fetch: ${variables.emailFetchTime}, Notifications: ${variables.notificationSendTime}`,
      )
      // Invalidate both config and status to refresh the data
      queryClient.invalidateQueries({ queryKey: serviceConfigKeys.all })
    },
    onError: (error) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to update configuration'
      toast.error(errorMessage)
    },
  })
}

export const useManualFetchEmails = () => {
  const queryClient = useQueryClient()
  const authedUser = useAuthStore((state) => state.user)

  return useMutation({
    mutationFn: serviceConfigApi.manualFetchEmails,
    onSuccess: (result: ManualFetchResponse) => {
      console.log('Manual fetch result:', result)
      toast.success(result.message)
      // Invalidate status to refresh the data
      queryClient.invalidateQueries({ queryKey: serviceConfigKeys.status() })
      queryClient.invalidateQueries({
        queryKey: [DONATIONS, authedUser?._id as string],
      })
    },
    onError: (error) => {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch emails'
      toast.error(errorMessage)
    },
  })
}

export const useManualSendNotifications = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: serviceConfigApi.manualSendNotifications,
    onSuccess: () => {
      toast.success(
        `Notifications Sent Successfully - Pending and Undeposited Cash Donations reported to treasurers and super admins`,
      )

      // Invalidate status to refresh the data
      queryClient.invalidateQueries({ queryKey: serviceConfigKeys.status() })
    },
    onError: (error) => {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to send notifications'
      toast.error(errorMessage)
    },
  })
}

export const useTriggerRecovery = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: serviceConfigApi.triggerRecovery,
    onSuccess: (result: RecoveryResponse) => {
      toast.success(result.message || 'Service recovery completed successfully')
      // Invalidate status to refresh the data
      queryClient.invalidateQueries({ queryKey: serviceConfigKeys.status() })
    },
    onError: (error) => {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to trigger recovery'
      toast.error(errorMessage)
    },
  })
}

export const useNotifyUnmatchedDonors = () => {
  return useMutation({
    mutationFn: serviceConfigApi.unmatchDonations,
    onSuccess: (result: RecoveryResponse) => {
      toast.success(
        result.message || 'Unmatched donors notification sent successfully',
      )
    },
    onError: (error) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to notify treasurers about unmatched donors'
      toast.error(errorMessage)
    },
  })
}

export const useFetchByReference = () => {
  const queryClient = useQueryClient()
  const authedUser = useAuthStore((state) => state.user)

  return useMutation({
    mutationFn: (referenceNumbers: string[]) => serviceConfigApi.fetchByReference(referenceNumbers),
    onSuccess: (response: FetchByReferenceResponse) => {
      const created = response.results.filter((r: FetchByReferenceResult) => r.status === 'created')
      if (created.length > 0) {
        toast.success(`${created.length} donation(s) created`)
        queryClient.invalidateQueries({
          queryKey: [DONATIONS, authedUser?._id as string],
        })
      } else {
        toast.info('No new donations created')
      }
    },
    onError: (error) => {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch e-transfers by reference'
      toast.error(errorMessage)
    },
  })
}

export const useFetchFromDate = () => {
  const queryClient = useQueryClient()
  const authedUser = useAuthStore((state) => state.user)

  return useMutation({
    mutationFn: (fromDate: string) => serviceConfigApi.fetchFromDate(fromDate),
    onSuccess: (result: FetchFromDateResponse) => {
      toast.success(result.message)
      queryClient.invalidateQueries({ queryKey: serviceConfigKeys.status() })
      queryClient.invalidateQueries({
        queryKey: [DONATIONS, authedUser?._id as string],
      })
    },
    onError: (error) => {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch e-transfers from date'
      toast.error(errorMessage)
    },
  })
}
